// Rival system (M3) — docs/ARCHITECTURE_M3.md §1/§3, design plan §7.4.
// Rivals never run the full economy: each is a strength process — an authored
// (or procedurally spawned) presence per market whose muscle tracks the funding
// environment and whose freshness follows a product-cycle sawtooth. They bear on
// the player through share/ASP pressure in market.ts, die in downturns (the
// survivor's reward), and become acquisition targets for ma.ts.

import type { ContentPack, MarketDef, RivalDef, RivalState } from "../types.js";
import type { TickContext } from "../ctx.js";
import { emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { clamp } from "../prng.js";

/** Resolve a rival's def: authored roster entry, or the def a procedural carries.
 *  Pure in (content, rivals) so bots and the UI can use it from PlayerView. */
export function rivalDefOf(content: ContentPack, rivals: Record<string, RivalState>, id: string): RivalDef {
  const st = rivals[id];
  if (st?.def) return st.def;
  const def = content.rivals.roster.find((r) => r.id === id);
  if (!def) throw new Error(`unknown rival ${id}`);
  return def;
}

export function rivalDef(ctx: TickContext, id: string): RivalDef {
  return rivalDefOf(ctx.content, ctx.s.rivals, id);
}

/** Product-cycle freshness sawtooth: 1.0 at release, decaying to 0.6 by cycle end. */
function freshness(cyclePos: number, cadenceMonths: number): number {
  return 1 - 0.4 * clamp(cyclePos / Math.max(1, cadenceMonths), 0, 1);
}

/** A rival's presence strength in one market at monthIndex m (0 if absent/dead). */
export function rivalStrengthIn(def: RivalDef, st: RivalState, marketId: string, m: number): number {
  if (!st.alive || def.foundedMonth > m) return 0;
  const presence = def.markets.find(
    (mk) => mk.marketId === marketId && mk.enterMonth <= m && (mk.exitMonth === undefined || m <= mk.exitMonth),
  );
  if (!presence) return 0;
  return def.baseStrength * st.scale * freshness(st.cyclePos, def.cadenceMonths);
}

/** Total live rival strength in a market — the denominator of share pressure. */
export function totalRivalStrength(ctx: TickContext, marketId: string): number {
  let total = 0;
  for (const id of Object.keys(ctx.s.rivals).sort()) {
    const st = ctx.s.rivals[id]!;
    if (!st.alive) continue;
    total += rivalStrengthIn(rivalDef(ctx, id), st, marketId, ctx.s.monthIndex);
  }
  return total;
}

/** Share/ASP pressure factor for a market (1 at the tuned rivalry baseline). */
export function rivalPressure(ctx: TickContext, market: MarketDef): number {
  const models = ctx.content.economy.models;
  return clamp(
    (1 + market.rivalryRef) / (1 + totalRivalStrength(ctx, market.id)),
    models.rivalPressureMin,
    models.rivalPressureMax,
  );
}

/** A rival's implied market share from presence strength (revenue proxy, M&A bundle). */
export function rivalImpliedShare(ctx: TickContext, strength: number): number {
  const models = ctx.content.economy.models;
  return Math.min(models.rivalShareCap, models.rivalShareCoeff * strength);
}

function fillTemplate(template: string, name: string, market: string): string {
  return template.replace(/\{name\}/g, name).replace(/\{market\}/g, market);
}

function newsFrom(ctx: TickContext, pool: string[], seedIdx: number, name: string, market: string): string {
  const t = pool.length > 0 ? pool[seedIdx % pool.length]! : `{name} — ${market}`;
  return fillTemplate(t, name, market);
}

/** Is the player present in this market (a released product, active or not-yet-EOL'd)? */
function playerInMarket(ctx: TickContext, marketId: string): boolean {
  return ctx.s.products.some((p) => p.marketId === marketId && p.active);
}

export function processRivals(ctx: TickContext): void {
  const { s, content } = ctx;
  if (s.dead) return;
  const m = s.monthIndex;
  const sentiment = interpolate(content.economy.sentiment, m);
  const pools = content.rivals.newsPools;

  // deterministic iteration order: sorted ids
  for (const id of Object.keys(s.rivals).sort()) {
    const st = s.rivals[id]!;
    if (!st.alive) continue;
    const def = rivalDef(ctx, id);
    if (def.foundedMonth > m) continue;

    // product cycle: a refresh resets freshness (news only where the player competes)
    st.cyclePos += ctx.monthsPerTick;
    if (st.cyclePos >= def.cadenceMonths) {
      st.cyclePos = 0;
      const active = def.markets.find((mk) => mk.enterMonth <= m && (mk.exitMonth === undefined || m <= mk.exitMonth));
      if (active && playerInMarket(ctx, active.marketId)) {
        const market = content.markets.find((x) => x.id === active.marketId);
        emit(ctx, "rivalNews", newsFrom(ctx, pools.release, m + id.length, def.displayName, market?.displayName ?? active.marketId), {
          rivalId: id,
          marketId: active.marketId,
        });
      }
    }

    // funding-driven scale: mean-reverts to what the era will fund
    const target = clamp(0.4 + def.fundingBeta * sentiment, 0.3, 2.0);
    st.scale += (target - st.scale) * 0.05 * ctx.monthsPerTick;
    st.scale *= ctx.rng.logNormal(0, 0.02);
    st.scale = clamp(st.scale, 0.2, 2.2);

    // death: weakness in a cold market, plus authored implosion windows
    let hazard = 0;
    if (st.scale < 0.5) hazard += def.deathHazardBase * (1 + 2 * Math.max(0, 0.5 - sentiment));
    if (def.fateWindow && m >= def.fateWindow.from && m <= def.fateWindow.to) hazard += def.fateWindow.hazard;
    if (hazard > 0 && ctx.rng.bernoulli(Math.min(0.9, hazard * ctx.monthsPerTick))) {
      st.alive = false;
      st.diedMonth = m;
      const firstMarket = def.markets[0];
      const market = content.markets.find((x) => x.id === firstMarket?.marketId);
      emit(ctx, "rivalDied", newsFrom(ctx, pools.death, m + id.length, def.displayName, market?.displayName ?? "the industry"), {
        rivalId: id,
        month: m,
      });
    }
  }

  // procedural spawns: hot markets in hot eras attract funded challengers
  const proc = content.rivals.proc;
  const aliveProcs = Object.keys(s.rivals).filter((id) => s.rivals[id]!.def && s.rivals[id]!.alive).length;
  if (sentiment > 0.65 && aliveProcs < proc.maxAlive) {
    for (const market of content.markets) {
      const tamNow = interpolate(market.tamUnitsPerMonth, m);
      const tamNext = interpolate(market.tamUnitsPerMonth, m + 12);
      if (tamNow < 50_000 || tamNext < tamNow * 1.25) continue; // only genuinely hot markets
      if (!ctx.rng.bernoulli(proc.spawnHazard * sentiment * ctx.monthsPerTick)) continue;
      const n = s.counters.nextRival;
      s.counters.nextRival += 1;
      const name = proc.names[(n - 1) % proc.names.length]!;
      const def: RivalDef = {
        id: `rival-proc-${n}`,
        displayName: name,
        archetype: "startup",
        path: market.pathAffinity === "neutral" ? "mixed" : market.pathAffinity,
        foundedMonth: m,
        baseStrength: proc.baseStrength,
        cadenceMonths: proc.cadenceMonths,
        fundingBeta: proc.fundingBeta,
        deathHazardBase: proc.deathHazardBase,
        acquirableFrom: m + proc.acquirableAfterMonths,
        markets: [{ marketId: market.id, enterMonth: m }],
      };
      s.rivals[def.id] = { alive: true, diedMonth: null, scale: 0.6, cyclePos: 0, acquiredByPlayer: false, def };
      emit(ctx, "rivalSpawned", newsFrom(ctx, pools.spawn, m + n, name, market.displayName), {
        rivalId: def.id,
        marketId: market.id,
      });
      break; // at most one spawn per month keeps the news ticker honest
    }
  }
}

/** Init helper for step.ts: authored roster starts alive (pre-founding rivals
 *  simply have zero strength until their foundedMonth). */
export function initRivals(roster: RivalDef[]): Record<string, RivalState> {
  const rivals: Record<string, RivalState> = {};
  for (const def of roster) {
    rivals[def.id] = { alive: true, diedMonth: null, scale: 0.6, cyclePos: 0, acquiredByPlayer: false };
  }
  return rivals;
}
